import React, { useContext, useState } from "react";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HelpIcon from "@mui/icons-material/Help";
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Snackbar,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { HardwareSensor } from "@safetyculture/s12-apis-connect-web/s12/iot/admin/v1/sensors_pb";
import { Vendor } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/vendor_pb";
import { HardwareSensorReading } from "@safetyculture/s12-apis-connect-web/s12/sensors/v1/hardware_sensor_reading_pb";

import {
  KnownUnits,
  SignalStrengthHelpURL,
  SignalStrengthMap,
  VendorMap,
  readingUnitToSymbolString,
} from "../../constants/sensors";
import { convertTimestampToDate } from "../../helpers/time";
import { UrlParamTypes } from "../../types/org-display";
import { getHardwareSensorDetails } from "api/endpoints/sensors";
import { callOptionsHeaders } from "api/helpers";
import { SnackbarContext } from "context/snackbar-context";

function CustomExportToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarExport
        // Removes printOptions as default toolbar option
        printOptions={{ disableToolbarButton: true }}
      />
    </GridToolbarContainer>
  );
}

function alertStatusToText(inAlert: boolean, theme: Theme) {
  return (
    <Chip
      size="small"
      style={{
        backgroundColor: inAlert
          ? theme.palette.error.main
          : theme.palette.background.default,
      }}
      label={inAlert ? "In alert" : "No alerts"}
    />
  );
}

function CustomNoRowsOverlay() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      height="100%"
    >
      <Box sx={{ mt: 1 }}>No results found.</Box>
    </Box>
  );
}

const signalStrengthFormatterChip = (
  signalStrength: string,
  vendor: string
) => {
  let chipProps = {
    color: "default" as
      | "default"
      | "error"
      | "warning"
      | "info"
      | "success"
      | "primary"
      | "secondary",
    label: "No Reading",
  };

  if (signalStrength) {
    const signalValue = parseFloat(signalStrength);
    if (!isNaN(signalValue)) {
      const signalMapper = SignalStrengthMap[vendor];
      switch (true) {
        case signalValue > signalMapper["good"]:
          chipProps = { color: "success", label: "Excellent" };
          break;
        case signalValue <= signalMapper["good"] &&
          signalValue > signalMapper["intermittent"]:
          chipProps = { color: "info", label: "Good" };
          break;
        case signalValue <= signalMapper["intermittent"] &&
          signalValue > signalMapper["poor"]:
          chipProps = { color: "warning", label: "Intermittent" };
          break;
        case signalValue <= signalMapper["poor"]:
          chipProps = { color: "error", label: "Poor" };
          break;
        default:
          chipProps = { color: "default", label: "No Reading" };
          break;
      }
    }
  }

  return <Chip {...chipProps} />;
};

function lowBatteryToText(lowBattery: boolean | undefined) {
  const color = lowBattery ? "warning" : "success";

  return <Chip size="small" color={color} label={lowBattery ? "Low" : "OK"} />;
}

function sensorOnlineStatusToText(online: boolean) {
  const color = online ? "success" : "error";
  return (
    <Chip
      size="small"
      color={color}
      label={online ? "Connected" : "Disconnected"}
    />
  );
}

const displayID = (row: HardwareSensor): string => {
  if (row.displayId.length > 0) {
    return row.displayId;
  }
  return row.vendorId;
};

const OrganisationSensorsTable = ({
  sensorsList,
  isLoading,
  setIsLoading,
}: {
  sensorsList: HardwareSensor[];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const theme = useTheme();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const [latestReadings, setLatestReadings] = useState<
    Map<string, HardwareSensorReading[]>
  >(new Map());
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [pageSize, setPageSize] = React.useState<number>(100);
  const [page, setPage] = React.useState<number>(0);
  // 100 rows is the maximum number of rows that can be rendered for MIT version of Data Grid
  const { copyToClipboard, showError } = useContext(SnackbarContext);

  const displayNotification = (message: string) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
  };

  const getLatestReadings = async (row: HardwareSensor) => {
    setIsLoading(true);
    return getHardwareSensorDetails(
      {
        sourceName: VendorMap[row.vendor],
        sourceId: row.vendorId,
        orgId: roleId,
      },
      callOptionsHeaders(regionCode, roleId)
    )
      .then((deviceReadings) => {
        setLatestReadings((currentReadings) => {
          setIsLoading(false);
          if (deviceReadings.latestReadings.length == 0) {
            displayNotification(
              "No readings available for device " + displayID(row)
            );
          }

          currentReadings.set(
            row.sensorId + row.vendorId,
            deviceReadings.latestReadings
          );
          return currentReadings;
        });
      })
      .catch((error) => {
        showError(error);
        setIsLoading(false);
      });
  };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Device Name",
      width: 180,
      renderCell({ row }) {
        return <Box textAlign="left">{row.name}</Box>;
      },
    },
    {
      field: "location",
      headerName: "Location",
      width: 110,
      renderCell({ row }) {
        return <Box textAlign="left">{row.location}</Box>;
      },
    },
    {
      field: "online",
      headerName: "Status",
      width: 150,
      align: "left",
      valueFormatter: ({ value }) => {
        return value ? "Connected" : "Disconnected";
      },
      renderCell({ row }) {
        return sensorOnlineStatusToText(row.online);
      },
    },
    {
      field: "signalStrength",
      headerName: "Signal",
      width: 140,
      align: "left",
      valueGetter({ row }) {
        return row.signalStrength ? parseInt(row.signalStrength) : "";
      },
      renderHeader: () => (
        <Box display="flex" marginTop="0.4rem">
          Signal
          <a href={SignalStrengthHelpURL} target={"_blank"} rel={"noreferrer"}>
            <HelpIcon
              style={{
                width: "15px",
                marginLeft: "0.3rem",
                marginTop: "1.5rem",
                color: theme.palette.primary.main,
              }}
            />
          </a>
        </Box>
      ),
      renderCell: ({ row }) =>
        signalStrengthFormatterChip(
          row.signalStrength,
          VendorMap[row.vendor as Vendor]
        ),
    },
    {
      field: "batteryLevel",
      headerName: "Battery level",
      width: 110,
      align: "left",
      valueFormatter: ({ value }) => (value ? "Low battery" : "OK"),
      valueGetter: ({ row }) => row.lowBattery,
      renderCell({ row }) {
        return lowBatteryToText(row.lowBattery);
      },
    },
    {
      field: "latestReadingTimestamp",
      headerName: "Latest readings",
      width: 190,
      // Disabled sorting on this because it shows data on the readings
      sortable: false,
      align: "left",
      valueGetter({ row }) {
        const rowLatestReadings = latestReadings.get(
          row.sensorId + row.vendorId
        );
        const rowHasReadings =
          !!rowLatestReadings && rowLatestReadings.length > 0;

        return rowHasReadings
          ? latestReadings
              .get(row.sensorId + row.vendorId)
              ?.map(
                (reading: HardwareSensorReading) =>
                  reading.name +
                  ": " +
                  reading.value +
                  readingUnitToSymbolString.get(reading.unit as KnownUnits)
              )
              .join("\n")
          : "Get Readings";
      },
      renderCell({ row }) {
        const rowLatestReadings = latestReadings.get(
          row.sensorId + row.vendorId
        );
        const rowHasReadings =
          !!rowLatestReadings && rowLatestReadings.length > 0;

        return rowHasReadings ? (
          <Box
            whiteSpace="normal"
            display="flex"
            flexDirection="column"
            textAlign="left"
          >
            {latestReadings
              .get(row.sensorId + row.vendorId)
              ?.map((reading: HardwareSensorReading) => {
                return (
                  <Box
                    key={row.sensorId + row.vendorId + reading.name}
                    whiteSpace="normal"
                    display="flex"
                    flexDirection="column"
                  >
                    <Typography fontSize={12}>
                      {reading.name +
                        ": " +
                        reading.value +
                        readingUnitToSymbolString.get(
                          reading.unit as KnownUnits
                        )}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        ) : (
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              getLatestReadings(row);
            }}
            data-testid="btn-get-latest-readings"
          >
            Get readings
          </Button>
        );
      },
    },
    {
      field: "readingTime",
      headerName: "Last Seen",
      width: 160,
      // Disabled sorting on this because multiple reading times could appear
      sortable: false,
      align: "left",
      valueGetter({ row }) {
        return row.latestReadingTimestamp
          ? DateTime.fromJSDate(
              convertTimestampToDate(row.latestReadingTimestamp)
            ).toRelative()
          : "";
      },
      renderCell({ row }) {
        const rowLatestReadings = latestReadings.get(
          row.sensorId + row.vendorId
        );
        const rowHasReadings =
          !!rowLatestReadings && rowLatestReadings.length > 0;

        return rowHasReadings ? (
          <Box whiteSpace="normal" display="flex" flexDirection="column">
            {latestReadings
              .get(row.sensorId + row.vendorId)
              ?.map((reading: HardwareSensorReading) => {
                return (
                  <Box
                    key={
                      row.vendorId +
                      row.sensorId +
                      reading.name +
                      reading.latestReadingTimestamp
                    }
                  >
                    <Typography fontSize={12}>
                      {reading.latestReadingTimestamp
                        ? DateTime.fromJSDate(
                            convertTimestampToDate(
                              reading.latestReadingTimestamp
                            )
                          ).toRelative()
                        : ""}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        ) : (
          <Box>
            {row.latestReadingTimestamp
              ? DateTime.fromJSDate(
                  convertTimestampToDate(row.latestReadingTimestamp)
                ).toRelative()
              : ""}
          </Box>
        );
      },
    },
    {
      field: "vendorId",
      headerName: "Device ID",
      width: 360,
      valueGetter: ({ row }) => displayID(row),
      renderCell({ row }) {
        const isDefinium =
          VendorMap[row.vendor as Vendor] === VendorMap[Vendor.DEFINIUM];
        return (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            textAlign="left"
          >
            <Box>
              {isDefinium ? (
                <Box>
                  <a
                    href={
                      "https://www.nnnco.io/admin/v3/#/assets/devices/" +
                      row.vendorId
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {row.vendorId}
                  </a>
                </Box>
              ) : (
                <Box
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displayID(row)}
                </Box>
              )}
            </Box>

            <Box>
              <IconButton onClick={() => copyToClipboard(displayID(row))}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "vendor",
      headerName: "Vendor",
      width: 110,
      align: "left",
      valueGetter: ({ row }) => VendorMap[row.vendor as Vendor],
    },
    {
      field: "liveMetricAlert",
      headerName: "Metric alert status",
      width: 150,
      align: "left",
      renderCell: ({ row }) => alertStatusToText(row.liveMetricAlert, theme),
      valueFormatter: ({ value }) => (value ? "In alert" : "No alerts"),
    },
    {
      field: "sourceID",
      headerName: "Source ID",
      width: 360,
      align: "left",
      valueGetter: ({ row }) => row.vendorId,
      renderCell: ({ row }) => (
        <Box
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {/* There is a source ID value */}
          {row.vendorId}
        </Box>
      ),
    },
    {
      field: "sensorId",
      headerName: "Internal sensor ID",
      width: 360,
      align: "left",
      renderCell({ row }) {
        return (
          <Box
            display="flex"
            whiteSpace="normal"
            textAlign="left"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "18rem",
                textAlign: "left",
              }}
            >
              {row.sensorId}
            </Box>
            <Box>
              <IconButton onClick={() => copyToClipboard(row.sensorId)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Box sx={{ height: 700, width: "100%" }}>
        <DataGrid
          rows={sensorsList}
          columns={columns}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          pageSizeOptions={[25, 50, 100]}
          getRowId={(row) => row.displayId || row.vendorId}
          getRowHeight={() => "auto"}
          disableRowSelectionOnClick
          components={{
            LoadingOverlay: LinearProgress,
            NoRowsOverlay: isLoading ? LinearProgress : CustomNoRowsOverlay,
            Toolbar: CustomExportToolbar,
          }}
          data-testid="org-sensor-table"
          disableVirtualization
          loading={isLoading}
          sortingOrder={["asc", "desc"]}
          initialState={{
            columns: {
              // This hides the sensorID column
              columnVisibilityModel: {
                sensorId: false,
              },
            },
            sorting: {
              sortModel: [{ field: "location", sort: "asc" }],
            },
            pagination: {
              paginationModel: { pageSize: 100 },
            },
          }}
          sx={{
            // Removes the default cell border on click
            "& .MuiDataGrid-cell:focus": {
              outline: "none",
            },
          }}
        />
      </Box>
      <Snackbar
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default OrganisationSensorsTable;
